import React, { useState, useEffect } from 'react';
import './TermsAndConditions.css';
import { motion } from 'framer-motion';
import { FaRegFileAlt, FaGavel, FaExclamationTriangle, FaUserShield, FaHandshake } from 'react-icons/fa';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { safeFetch } from '../utils/apiUtils';
import useSEO, { SITE_URL } from '../utils/useSEO';

const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.6, ease: "easeOut" }
    }
};

const getIcon = (type) => {
    switch (type) {
        case 'gavel': return <FaGavel />;
        case 'file': return <FaRegFileAlt />;
        case 'shield': return <FaUserShield />;
        case 'alert': return <FaExclamationTriangle />;
        case 'handshake': return <FaHandshake />;
        default: return <FaGavel />;
    }
};

const TermsAndConditions = () => {
    useSEO({
        title:       "Terms & Conditions | Nimai Solutions",
        description: "Review the terms and conditions governing the use of Nimai Solutions' web development, mobile app, and design services.",
        canonical:   `${SITE_URL}/terms-conditions`,
    });

    const [terms, setTerms] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const fetchTerms = async () => {
            try {
                const { ok, data: result } = await safeFetch('https://aayushrealproperties.in/nimaibackend/public/api/term-conditions');
                let termsToSet = null;

                if (Array.isArray(result)) {
                    termsToSet = result[0];
                } else if (result.data) {
                    termsToSet = Array.isArray(result.data) ? result.data[0] : result.data;
                }

                if (ok && termsToSet) {
                    setTerms(termsToSet);
                }
            } catch (error) {
                console.error('Error fetching terms:', error);
            } finally {
                setIsLoading(false);
            }
        };
        fetchTerms();
    }, []);

    if (isLoading) {
        return (
            <div className="terms-loading">
                <div className="loader"></div>
            </div>
        );
    }

    // Default data if none exists in API
    const displayData = terms || {
        hero_title_part1: 'Terms',
        hero_highlight_symbol: '&',
        hero_title_suffix: 'Conditions',
        hero_description: "Please read these terms carefully before using Nimai Solutions' website and services.",
        last_updated_date: 'February 6, 2026',
        sections: []
    };

    return (
        <div className="terms-container">
            <Navbar />

            <section className="terms-hero">
                <div className="terms-hero-bg-shape"></div>
                <motion.div
                    className="terms-hero-content"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                >
                    <h1>{displayData.hero_title_part1} <span>{displayData.hero_highlight_symbol}</span> {displayData.hero_title_suffix}</h1>
                    <p>{displayData.hero_description}</p>
                </motion.div>
            </section>

            <div className="terms-content-wrapper">
                <motion.div
                    className="terms-card"
                    variants={fadeInUp}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                >
                    {displayData.sections.map((section, index) => (
                        <div key={index} className="terms-section">
                            <h2>{getIcon(section.icon_type)} {index + 1}. {section.section_title}</h2>
                            <p>{section.content}</p>
                            {section.list_items && section.list_items.length > 0 && (
                                <ul className="terms-list">
                                    {section.list_items.map((item, i) => (
                                        <li key={i}>{item}</li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    ))}

                    <div className="last-updated">
                        <span>Last Updated: {displayData.last_updated_date}</span>
                        <span onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} style={{ cursor: 'pointer', color: 'var(--accent-orange)', fontWeight: '600' }}>Back to top ↑</span>
                    </div>
                </motion.div>
            </div>

            <Footer />
        </div>
    );
};

export default TermsAndConditions;
